import React from "react";

interface ShipmentDetails {
    transportation: string;
    otherReferences: string;
    countryOfTransshipment: string;
    conditionsOfSale: string;
    netWeight?: number;
    grossWeight?: number;
    weightUnit: string;
}

interface ShipmentDetailsSectionProps {
    shipmentDetails: ShipmentDetails;
    onInputChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => void;
}

export const ShipmentDetailsSection: React.FC<ShipmentDetailsSectionProps> = ({
    shipmentDetails,
    onInputChange
}) => {
    return (
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-4 sm:p-6">
            <h3 className="text-lg font-medium mb-3 sm:mb-4">Shipment Details</h3>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                    <label htmlFor="shipmentDetails.conditionsOfSale" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Conditions of Sale
                    </label>
                    <input
                        type="text"
                        id="shipmentDetails.conditionsOfSale"
                        name="shipmentDetails.conditionsOfSale"
                        value={shipmentDetails.conditionsOfSale}
                        onChange={onInputChange}
                        placeholder="Sale, Intercompany transfer, Return, etc."
                        className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white text-sm"
                    />
                </div>
                <div>
                    <label htmlFor="shipmentDetails.transportation" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Transportation Method
                    </label>
                    <input
                        type="text"
                        id="shipmentDetails.transportation"
                        name="shipmentDetails.transportation"
                        value={shipmentDetails.transportation}
                        onChange={onInputChange}
                        placeholder="Transportation mode"
                        required
                        className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white text-sm"
                    />
                </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
                <div>
                    <label htmlFor="shipmentDetails.countryOfTransshipment" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Country of Transhipment <span className="text-gray-500 text-xs">(if applicable)</span>
                    </label>
                    <input
                        type="text"
                        id="shipmentDetails.countryOfTransshipment"
                        name="shipmentDetails.countryOfTransshipment"
                        value={shipmentDetails.countryOfTransshipment}
                        onChange={onInputChange}
                        placeholder="Country of transhipment, if applicable"
                        className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white text-sm"
                    />
                </div>
                <div>
                    <label htmlFor="shipmentDetails.otherReferences" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Other References (Include Purchaser's Order Number)
                    </label>
                    <input
                        type="text"
                        id="shipmentDetails.otherReferences"
                        name="shipmentDetails.otherReferences"
                        value={shipmentDetails.otherReferences}
                        onChange={onInputChange}
                        placeholder="PO#, Tracking#, etc."
                        className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white text-sm"
                    />
                </div>
            </div>

            {/* Weight section */}
            <div className="mt-4">
                <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Shipment Weight</h4>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div>
                        <label htmlFor="shipmentDetails.netWeight" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Net Weight
                        </label>
                        <input
                            type="number"
                            id="shipmentDetails.netWeight"
                            name="shipmentDetails.netWeight"
                            value={shipmentDetails.netWeight || ''}
                            onChange={onInputChange}
                            placeholder="Net weight"
                            step="0.01"
                            min="0"
                            className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white text-sm"
                        />
                    </div>
                    <div>
                        <label htmlFor="shipmentDetails.grossWeight" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Gross Weight
                        </label>
                        <input
                            type="number"
                            id="shipmentDetails.grossWeight"
                            name="shipmentDetails.grossWeight"
                            value={shipmentDetails.grossWeight || ''}
                            onChange={onInputChange}
                            placeholder="Gross weight"
                            step="0.01"
                            min="0"
                            className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white text-sm"
                        />
                    </div>
                    <div>
                        <label htmlFor="shipmentDetails.weightUnit" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Weight Unit
                        </label>
                        <select
                            id="shipmentDetails.weightUnit"
                            name="shipmentDetails.weightUnit"
                            value={shipmentDetails.weightUnit}
                            onChange={onInputChange}
                            className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white text-sm"
                        >
                            <option value="kg">Kilograms (kg)</option>
                            <option value="lb">Pounds (lb)</option>
                        </select>
                    </div>
                </div>
            </div>
        </div>
    );
};
